<script lang="ts">import BetsLayout from "./parts/bets-layout.svelte";
import { BetType } from "./types/enums";
import { FeatureSet, ReleaseStage, stateFeatureSet, stateThemeSetting, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { getRaces, isMobile } from "@infinitro/global";
import { InfoCard } from "@infinitro/info-card";
import { LeaderboardCard } from "@infinitro/races";
import { Tabs } from "@infinitro/tabs";
import { Text } from "@infinitro/text";
// Active tabs
let activeTab;
let tabs;
let weeklyRaceId = "current";
let loadingWeeklyRace = false;
let isWeeklyRaceEnabled = false;
const LeaderboardCardAny = LeaderboardCard;
$: {
	tabs = isWeeklyRaceEnabled ? [{
		label: `${$t(Verbiage.latest_bets)}`,
		value: BetType.AllBets
	}, {
		label: `${$t(Verbiage.weekly_race)}`,
		value: BetType.Races
	}] : [{
		label: `${$t(Verbiage.bets)}`,
		value: BetType.AllBets
	}];
}
function handleTabSelected(detail) {
	activeTab = detail.value;
	if (activeTab === BetType.Races && isWeeklyRaceEnabled) {
		resolveWeeklyRaceId();
	}
}
function toRaceDate(value) {
	if (value instanceof Date) return value;
	if (typeof value === "string" || typeof value === "number") {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? undefined : parsed;
	}
	return undefined;
}
async function resolveWeeklyRaceId() {
	if (loadingWeeklyRace) return;
	loadingWeeklyRace = true;
	try {
		const races = await getRaces() ?? [];
		const now = Date.now();
		const hasCurrentRace = races.some((race) => {
			const startDate = toRaceDate(race.startDate);
			const endDate = toRaceDate(race.endDate);
			const isInDateWindow = startDate !== undefined && endDate !== undefined ? now >= startDate.getTime() && now <= endDate.getTime() : false;
			return race.active === true || isInDateWindow;
		});
		weeklyRaceId = hasCurrentRace ? "current" : "last";
	} catch {
		weeklyRaceId = "last";
	} finally {
		loadingWeeklyRace = false;
	}
}
$: useAlternative2 = $stateUi.betfeed.betfeedStyle === "alternative2";
$: usedVerbiage = $stateUi.betfeed.usedVerbiage;
$: usedVerbiageKey = usedVerbiage;
$: isWeeklyRaceEnabled = $stateFeatureSet === FeatureSet.Preview && wantAtLeastReleaseStage(ReleaseStage.Alpha) && $stateThemeSetting.racesEnabled === true && $stateUi.betfeed.enableWeeklyRaceTab === true;
$: if (!activeTab) {
	activeTab = BetType.AllBets;
}
$: if (!isWeeklyRaceEnabled && activeTab !== BetType.AllBets) {
	activeTab = BetType.AllBets;
}
</script>

<InfoCard
	width="100%"
	radialGradient2GlowStartColor={useAlternative2 ? 'rgba(248, 60, 83, 0.20)' : ''}
>
	<div class="bet-feed-container">
		<div class="title-tabs-container">
			<div class="title-container">
				<span class="live-dot"></span>
				{#key $isMobile}
					<Text
						subtitleSize={'large'}
						type={'subtitle'}
						subtitleWeight="bold"
						customStyle="font-size: {$isMobile && isWeeklyRaceEnabled ? '16px' : '22px'};"
						>{usedVerbiage && Verbiage[usedVerbiageKey] in Verbiage
							? $t(Verbiage[usedVerbiageKey])
							: $t(Verbiage.live)}</Text
					>
				{/key}
			</div>

			<div class="bet-feed-tabs-container">
				{#if isWeeklyRaceEnabled}
					<Tabs {activeTab} onChange={handleTabSelected} size="small" {tabs} />
				{/if}
			</div>
		</div>
		{#if activeTab === BetType.AllBets}
			<div class="bet-feed-content">
				<BetsLayout />
			</div>
		{:else if activeTab === BetType.Races && isWeeklyRaceEnabled}
			<div class="bet-feed-content">
				<svelte:component
					this={LeaderboardCardAny}
					maxLength={3}
					raceId={weeklyRaceId}
					useBetFeedStyles={true}
				/>
			</div>
		{/if}
	</div>
</InfoCard>

<style>
	.bet-feed-container {
		width: 100%;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.title-tabs-container {
		display: flex;
		min-height: 52px;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
}

	.title-container {
		display: flex;
		align-items: center;
}

	.live-dot {
		margin-right: 0.5rem;
		display: inline-block;
		height: 9px;
		width: 9px;
		border-radius: 9999px;
		background-color: var(--color-error-500);
		animation: pulsate 1.5s infinite;
}

	.bet-feed-tabs-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding-left: 0px;
}

	.bet-feed-content {
		width: 100%;
}

	@keyframes pulsate {
		0%,
		100% {
			box-shadow:
				0 0 10px 5px var(--color-error-500),
				0 0 5px 0 var(--color-error-500);
		}
		50% {
			box-shadow:
				0 0 5px 5px color-mix(in srgb, var(--color-error-500), transparent 100%),
				0 0 5px 0 var(--color-error-500);
		}
	}
</style>
